import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { formatPln } from '../catalog/money';
import { leaveWithoutAsking } from '../ui/unsaved';
import { SUBRENTALS_KEY, type MatchCandidate, type MatchResult } from './types';

/** The name a candidate is shown with: our item's (a position of our item) or the supplier's own. */
export const candidateName = (c: MatchCandidate): string => c.itemName ?? c.name ?? '';

/** „Szmyt Audio — Alcons Audio LR7 — 250,00 zł / doba — ma 8 szt.” */
function CandidateLabel({ c }: { c: MatchCandidate }) {
  return (
    <>
      {c.supplierName && <strong>{c.supplierName}</strong>}
      {c.supplierName && ' — '}
      {candidateName(c)}
      {c.priceDayGrosze !== undefined && ` — ${formatPln(c.priceDayGrosze)} / doba`}
      {c.quantity != null && ` — ma ${c.quantity} szt.`}
      {c.match === 'similar' && <span className="muted"> (podobna nazwa)</span>}
      {c.match === 'other_item' && <span className="muted"> (podpięta pod {c.itemName ? `„${c.itemName}”` : 'inne urządzenie'})</span>}
    </>
  );
}

/**
 * „Pozycja dostawcy” in „Dopożycz” (owner 2026-10-23 21:23): on opening, the suppliers' positions for our item are
 * looked up — linked to it (exact) or with a similar name. The best exact one is chosen at once (a supplier of this
 * event first, then the most rented, then the cheapest); a similar name is never chosen silently: „Wybierz pozycję
 * dostawcy” lists them. „Wyszukaj” searches the suppliers' catalogs by a text (the item's name, editable); „Wskaż w
 * katalogu” opens them in the picking mode (`pickHref`).
 */
export function SupplierPositionPicker({
  itemId,
  itemName,
  eventId,
  chosen,
  onChoose,
  pickHref,
  auto: autoChoose = true,
}: {
  itemId: string;
  itemName: string;
  eventId: string;
  chosen: MatchCandidate | null;
  /** `auto`: chosen by the matching (not by the person). */
  onChoose: (c: MatchCandidate | null, how: 'auto' | 'person') => void;
  pickHref: string;
  /** False: the person pointed at a position already („Wskaż w katalogu”) — the matching does not choose (review I1). */
  auto?: boolean;
}) {
  const navigate = useNavigate();
  const [listOpen, setListOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [text, setText] = useState(itemName);
  const [query, setQuery] = useState('');
  const auto = useQuery({
    queryKey: [...SUBRENTALS_KEY, 'match', itemId, eventId],
    queryFn: () => api<MatchResult>(`/api/subrentals/match?itemId=${encodeURIComponent(itemId)}&eventId=${encodeURIComponent(eventId)}`),
  });
  const search = useQuery({
    queryKey: [...SUBRENTALS_KEY, 'match', itemId, eventId, 'q', query],
    enabled: query !== '',
    queryFn: () => api<MatchResult>(`/api/subrentals/match?itemId=${encodeURIComponent(itemId)}&eventId=${encodeURIComponent(eventId)}&q=${encodeURIComponent(query)}`),
  });
  // the matching chooses once per item, and only while the person has chosen nothing
  const chosenOnce = useRef<string | null>(null);
  useEffect(() => {
    if (!auto.data || chosenOnce.current === itemId) return;
    chosenOnce.current = itemId;
    if (chosen || !autoChoose) return;
    const best = auto.data.candidates.find((c) => c.priceId === auto.data.pickedPriceId);
    if (best) onChoose(best, 'auto');
  }, [auto.data, itemId, chosen, onChoose, autoChoose]);

  const shown = query !== '' ? search : auto;
  const list = shown.data?.candidates ?? [];
  const showList = !chosen || listOpen || searching;
  const pick = (c: MatchCandidate) => {
    onChoose(c, 'person');
    setListOpen(false);
    setSearching(false);
  };
  const error = shown.error instanceof ApiError ? shown.error.message : shown.error ? 'Nie udało się wyszukać w katalogach dostawców' : '';

  return (
    <div className="group rental-position" role="group" aria-label="Pozycja dostawcy">
      {chosen && (
        <p className="rental-item rental-position-chosen">
          <span className="muted">Pozycja dostawcy</span>{' '}
          <span>
            <CandidateLabel c={chosen} />
          </span>{' '}
          {!showList && (
            <button type="button" className="btn small ghost" onClick={() => setListOpen(true)}>
              Zmień
            </button>
          )}
        </p>
      )}
      {showList && (
        <>
          {searching && (
            <div
              className="field-row rental-position-search"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setQuery(text.trim());
                }
              }}
            >
              <label className="field">
                <span>Szukaj w katalogach dostawców</span>
                <input type="search" value={text} autoFocus onChange={(e) => setText(e.target.value)} />
              </label>
              <button type="button" className="btn" onClick={() => setQuery(text.trim())}>
                Szukaj
              </button>
            </div>
          )}
          {shown.isLoading && shown.fetchStatus !== 'idle' && (
            <p className="muted small" role="status">
              Szukam w katalogach dostawców…
            </p>
          )}
          {error && (
            <p className="alert" role="alert">
              {error}
            </p>
          )}
          {shown.data && list.length > 0 && (
            <fieldset className="rental-candidates">
              <legend>Wybierz pozycję dostawcy</legend>
              <ul>
                {list.map((c) => (
                  <li key={c.priceId}>
                    <button
                      type="button"
                      className={`btn small${chosen?.priceId === c.priceId ? ' primary' : ''}`}
                      aria-pressed={chosen?.priceId === c.priceId}
                      // review C1: a position of another item of ours cannot be chosen for this one
                      disabled={c.match === 'other_item'}
                      onClick={() => pick(c)}
                    >
                      <CandidateLabel c={c} />
                    </button>
                  </li>
                ))}
              </ul>
            </fieldset>
          )}
          {shown.data && list.length === 0 && (
            <p className="muted small">{query !== '' ? `Nic nie znaleziono dla „${query}”.` : `Żaden dostawca nie ma w katalogu pozycji podobnej do „${itemName}”.`}</p>
          )}
          <div className="actions rental-position-actions">
            {!searching && (
              <button type="button" className="btn small" onClick={() => setSearching(true)}>
                Wyszukaj
              </button>
            )}
            {/* the open form goes along in the address (sprzęt, ilość, dni) and comes back: nothing to ask about */}
            <button type="button" className="btn small" onClick={() => leaveWithoutAsking(() => navigate(pickHref))}>
              Wskaż w katalogu
            </button>
            {chosen && (
              <button
                type="button"
                className="btn small ghost"
                onClick={() => {
                  setListOpen(false);
                  setSearching(false);
                }}
              >
                Zostaw wybraną
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
